import type { ProviderDetail } from '../../../../../js/api';
import { MalpracticeHistorySection } from '../../../components/malpractice-history-section';
import { DocumentSections, DocumentSectionSpec } from './DocumentSections';
import { LegacyUploadsNote } from './LegacyUploadsNote';
import {
  fileCountFor,
  formatFileCount,
  ProviderDrawerMaps,
  providerFileName,
} from './providerDisplay';

/** `ProviderFileType` code of the LEGACY malpractice insurance uploads (files only, so a count). */
export const INSURANCE_FILE_TYPE = 'COI';

/**
 * DocumentType slug of the certificates of insurance a clinician supplies for past
 * work (code `COIHX`, "Certification of Insurance (Historical)") — proof of coverage
 * for credentialing. Not the `certificate-of-insurance` type, which is the COI Sendit
 * generates for a connection or assignment.
 */
export const COI_HISTORY_TYPE_SLUG = 'coi-history';

const INSURANCE_DOCUMENT_TYPES: DocumentSectionSpec[] = [
  {
    slug: COI_HISTORY_TYPE_SLUG,
    title: (count) => `${count === 1 ? 'Certificate' : 'Certificates'} of Insurance (Historical)`,
    testId: 'provider-insurance',
  },
];

/**
 * Malpractice & other history (the same questionnaire summary + dialog the connection
 * drawer's Presentation Info shows) and the clinician's certificates of insurance (the
 * standard document sections).
 */
export function InsuranceTab({
  provider,
  maps,
  onOpenDocuments,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
  /** Switches the drawer to the Documents tab (legacy uploads live there). */
  onOpenDocuments: () => void;
}) {
  const legacyCount = fileCountFor(provider.file_counts, INSURANCE_FILE_TYPE);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {/* Full width: the questions and comments are long-form text, not label/value pairs. */}
      <MalpracticeHistorySection providerId={provider.id} viewControl="icon" />

      <DocumentSections
        providerId={provider.id}
        types={INSURANCE_DOCUMENT_TYPES}
        testIdPrefix="provider-insurance"
        zipSubject={`${providerFileName(provider)} Insurance Documents`}
        noneOnFileLead="No insurance certificates on file — add:"
        emptyText="No insurance certificates on file."
      />
      {legacyCount > 0 && (
        <LegacyUploadsNote
          summary={`${
            maps.ProviderFileType?.[INSURANCE_FILE_TYPE] || INSURANCE_FILE_TYPE
          } uploads not yet in Documents: ${formatFileCount(legacyCount)}.`}
          onOpenDocuments={onOpenDocuments}
          data-testid="provider-insurance-legacy"
        />
      )}
    </div>
  );
}
